// A model's list price is owned here: the `pricing` field on its definition. The `cost` label
// every surface shows is derived from it, never authored, so the two cannot disagree.

const UNIT_SUFFIX = Object.freeze({
  image: '/image',
  megapixel: '/MP',
  second: '/s',
  minute: '/min',
  kchars: '/1k chars',
  generation: '',
});
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MAX_NOTE_LENGTH = 40;

export const MODEL_PRICING_UNITS = Object.freeze(Object.keys(UNIT_SUFFIX));

function normalizeUsd(value, label) {
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value < 0) throw new Error(`${label} usd must be a non-negative number.`);
    return value;
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const min = normalizeUsd(value.min, label);
    const max = normalizeUsd(value.max, label);
    if (min > max) throw new Error(`${label} usd min must not exceed max.`);
    return { min, max };
  }
  throw new Error(`${label} usd must be a number or { min, max }.`);
}

/**
 * Validates a definition's `pricing`. `null` (or absent) is "no price on file". A malformed
 * object throws, so a typo fails at registry build instead of reaching the agent as a price.
 */
export function normalizeModelPricing(pricing, label = 'Model pricing') {
  if (pricing == null) return null;
  if (typeof pricing !== 'object' || Array.isArray(pricing)) {
    throw new Error(`${label} must be an object or null.`);
  }
  const note = typeof pricing.note === 'string' ? pricing.note.trim() : '';
  if (note.length > MAX_NOTE_LENGTH) throw new Error(`${label} note must be ${MAX_NOTE_LENGTH} characters or fewer.`);
  if (pricing.unit === undefined && pricing.usd === undefined) {
    // Unpriced, with a note saying how the model is billed instead ("Billed in APIFrame credits").
    if (!note) throw new Error(`${label} without a unit and usd must carry a note.`);
    return { unit: null, usd: null, note };
  }
  if (!Object.hasOwn(UNIT_SUFFIX, pricing.unit)) {
    throw new Error(`${label} unit must be one of ${MODEL_PRICING_UNITS.join(', ')}.`);
  }
  const usd = normalizeUsd(pricing.usd, label);
  if (typeof pricing.asOf !== 'string' || !ISO_DATE_PATTERN.test(pricing.asOf)
    || Number.isNaN(Date.parse(pricing.asOf))) {
    throw new Error(`${label} asOf must be an ISO date (YYYY-MM-DD).`);
  }
  return { unit: pricing.unit, usd, asOf: pricing.asOf, ...(note ? { note } : null) };
}

// Cents always show two places ($0.40); finer prices keep up to four ($0.034, $0.2205).
function formatUsd(value) {
  const rounded = Number(value.toFixed(4));
  const decimals = (String(rounded).split('.')[1] || '').length;
  return `$${rounded.toFixed(Math.max(2, decimals))}`;
}

/** The display label for a normalized `pricing`, e.g. `~$0.17/s` or `~$0.004-$0.40/image`. */
export function formatModelPricing(pricing) {
  if (!pricing) return null;
  if (pricing.usd == null) return pricing.note;
  const maxUsd = typeof pricing.usd === 'number' ? pricing.usd : pricing.usd.max;
  if (maxUsd === 0) return `Free${pricing.note ? ` — ${pricing.note}` : ''}`;
  const amount = typeof pricing.usd === 'number'
    ? formatUsd(pricing.usd)
    : `${formatUsd(pricing.usd.min)}-${formatUsd(pricing.usd.max)}`;
  return `~${amount}${UNIT_SUFFIX[pricing.unit]}${pricing.note ? ` ${pricing.note}` : ''}`;
}

/** The `{ min, max }` USD price per `unit`, or null when the model is unpriced or priced in another unit. */
export function getModelUnitPrice(model, unit) {
  const pricing = model?.pricing;
  if (!pricing || pricing.usd == null || pricing.unit !== unit) return null;
  return typeof pricing.usd === 'number'
    ? { min: pricing.usd, max: pricing.usd }
    : { min: pricing.usd.min, max: pricing.usd.max };
}

const SECONDS_PER_UNIT = Object.freeze({ second: 1, minute: 60 });

/**
 * The estimated cost of running a duration-billed model over `durationSec` of media, e.g.
 * `~$0.05 for 30s at ~$0.10/min`, from the definition's list price. Null when the length is
 * unknown or the model is not priced per second or minute.
 */
export function formatModelDurationCostEstimate(model, durationSec) {
  const pricing = model?.pricing;
  const unitSeconds = SECONDS_PER_UNIT[pricing?.unit];
  const unitPrice = unitSeconds ? getModelUnitPrice(model, pricing.unit) : null;
  if (!unitPrice || !(durationSec > 0)) return null;
  const seconds = Math.round(durationSec);
  const units = durationSec / unitSeconds;
  const min = formatUsd(unitPrice.min * units);
  const max = formatUsd(unitPrice.max * units);
  return `~${min === max ? min : `${min}-${max}`} for ${seconds}s at ${formatModelPricing(pricing)}`;
}
